import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import DeleteButton from '../components/DeleteButton';
import { uploadMultipart, downloadAuthenticatedFile } from '../services/hdpFiles';
import EvidenceTimezoneToggle from '../components/EvidenceTimezoneToggle';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';

function Evidence() {
  const { mode, setMode, format, localLabel, suffix } = useEvidenceTimezone();
  const [evidence, setEvidence] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState(null);
  const [activeTab, setActiveTab] = useState('evidence');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [evidenceForm, setEvidenceForm] = useState({
    title: '',
    evidence_type: 'note',
    source: '',
    content: '',
    case_id: '',
    notes: '',
  });
  const [indicatorForm, setIndicatorForm] = useState({
    ioc_type: 'domain',
    value: '',
    confidence: 'medium',
    source: '',
    case_id: '',
    notes: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [evidenceRes, indicatorsRes, casesRes, metaRes] = await Promise.all([
        api.get('/hdp/evidence'),
        api.get('/hdp/iocs'),
        api.get('/hdp/cases'),
        api.get('/hdp/meta'),
      ]);
      setEvidence(evidenceRes.data.evidence || []);
      setIndicators(indicatorsRes.data.iocs || []);
      setCases(casesRes.data.cases || []);
      setMeta(metaRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const submitEvidence = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('file', attachmentFile);
        formData.append('title', evidenceForm.title || attachmentFile.name);
        formData.append('evidence_type', evidenceForm.evidence_type);
        formData.append('source', evidenceForm.source || '');
        formData.append('content', evidenceForm.content || '');
        formData.append('notes', evidenceForm.notes || '');
        if (evidenceForm.case_id) formData.append('case_id', evidenceForm.case_id);
        await uploadMultipart('/hdp/evidence/upload', formData);
      } else {
        await api.post('/hdp/evidence', {
          ...evidenceForm,
          case_id: evidenceForm.case_id || undefined,
        });
      }
      setAttachmentFile(null);
      setEvidenceForm({
        title: '',
        evidence_type: 'note',
        source: '',
        content: '',
        case_id: '',
        notes: '',
      });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save evidence');
    }
  };

  const submitIndicator = async (e) => {
    e.preventDefault();
    await api.post('/hdp/iocs', {
      ...indicatorForm,
      case_id: indicatorForm.case_id || undefined,
    });
    setIndicatorForm({
      ioc_type: 'domain',
      value: '',
      confidence: 'medium',
      source: '',
      case_id: '',
      notes: '',
    });
    loadData();
  };

  const handleDownload = async (item) => {
    if (!item.download_url) return;
    try {
      await downloadAuthenticatedFile(
        item.download_url.replace('/api', ''),
        item.original_filename || item.title
      );
    } catch (err) {
      setError(err.message || 'Download failed');
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Evidence Vault</h1>
        <div className="header-actions">
          <EvidenceTimezoneToggle mode={mode} setMode={setMode} localLabel={localLabel} />
        </div>
      </header>

      <div className="main-body">
        {error && (
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)', borderColor: 'var(--neon-red)' }}>
            <div className="card-body" style={{ color: 'var(--neon-red)' }}>{error}</div>
          </div>
        )}

        <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="card-body">
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Capture, catalog, and enrich artifacts from suspicious actors. Attach files such as
              screenshots, documents, chat logs, and resumes tied to investigation cases.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
          <button
            className={`btn ${activeTab === 'evidence' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('evidence')}
          >
            Evidence
          </button>
          <button
            className={`btn ${activeTab === 'indicators' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('indicators')}
          >
            Indicators
          </button>
        </div>

        {activeTab === 'evidence' ? (
          <>
            <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div className="card-header">
                <h3 className="card-title">Add Evidence</h3>
              </div>
              <form className="card-body" onSubmit={submitEvidence}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input
                      className="form-input"
                      value={evidenceForm.title}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, title: e.target.value })}
                      required={!attachmentFile}
                      placeholder={attachmentFile ? attachmentFile.name : 'Evidence title'}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-input"
                      value={evidenceForm.evidence_type}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, evidence_type: e.target.value })}
                    >
                      {(meta?.evidence_types || []).map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Case</label>
                    <select
                      className="form-input"
                      value={evidenceForm.case_id}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, case_id: e.target.value })}
                    >
                      <option value="">Unassigned</option>
                      {cases.map((item) => (
                        <option key={item.case_id} value={item.case_id}>{item.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Attach File</label>
                  <input
                    type="file"
                    className="form-input"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  />
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px' }}>
                    Files are quarantined on upload, hashed, and never executed on the server.
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <input
                    className="form-input"
                    placeholder="Agent session, manual capture, external feed"
                    value={evidenceForm.source}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, source: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Content / Summary</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={evidenceForm.content}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, content: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  {attachmentFile ? 'Upload Evidence' : 'Save Evidence'}
                </button>
              </form>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Evidence Items ({evidence.length})</h3>
              </div>
              <div className="card-body">
                {loading ? (
                  <p style={{ color: 'var(--text-muted)' }}>Loading evidence...</p>
                ) : evidence.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)' }}>No evidence captured yet.</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Type</th>
                          <th>File</th>
                          <th>Case</th>
                          <th>SHA256</th>
                          <th>Collected ({suffix})</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {evidence.map((item) => (
                          <tr key={item.evidence_id}>
                            <td>{item.title}</td>
                            <td>{item.evidence_type}</td>
                            <td>{item.original_filename || (item.has_file ? 'Attached' : '—')}</td>
                            <td>{item.case_title || 'Unassigned'}</td>
                            <td><code style={{ fontSize: '11px' }}>{item.sha256 ? `${item.sha256.slice(0, 12)}...` : '—'}</code></td>
                            <td className="timestamp-cell">{format(item.collected_at)}</td>
                            <td style={{ display: 'flex', gap: '6px' }}>
                              {item.has_file && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleDownload(item)}
                                >
                                  Download
                                </button>
                              )}
                              <DeleteButton
                                confirmMessage={`Delete evidence "${item.title}"?`}
                                onDelete={async () => {
                                  await api.delete(`/hdp/evidence/${item.evidence_id}`);
                                  setEvidence((prev) => prev.filter((row) => row.evidence_id !== item.evidence_id));
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div className="card-header">
                <h3 className="card-title">Add Indicator</h3>
              </div>
              <form className="card-body" onSubmit={submitIndicator}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-input"
                      value={indicatorForm.ioc_type}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, ioc_type: e.target.value })}
                    >
                      {(meta?.ioc_types || []).map((type) => (
                        <option key={type} value={type}>
                          {meta?.ioc_type_labels?.[type] || type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Value</label>
                    <input
                      className="form-input"
                      value={indicatorForm.value}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, value: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confidence</label>
                    <select
                      className="form-input"
                      value={indicatorForm.confidence}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, confidence: e.target.value })}
                    >
                      {(meta?.confidence_levels || []).map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Case</label>
                    <select
                      className="form-input"
                      value={indicatorForm.case_id}
                      onChange={(e) => setIndicatorForm({ ...indicatorForm, case_id: e.target.value })}
                    >
                      <option value="">Unassigned</option>
                      {cases.map((item) => (
                        <option key={item.case_id} value={item.case_id}>{item.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary">Save Indicator</button>
              </form>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Indicators ({indicators.length})</h3>
              </div>
              <div className="card-body">
                {loading ? (
                  <p style={{ color: 'var(--text-muted)' }}>Loading indicators...</p>
                ) : indicators.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)' }}>No indicators recorded yet.</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Value</th>
                          <th>Confidence</th>
                          <th>Case</th>
                          <th>Last Seen ({suffix})</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {indicators.map((item) => (
                          <tr key={item.indicator_id}>
                            <td>{item.ioc_type}</td>
                            <td><code>{item.value}</code></td>
                            <td>{item.confidence}</td>
                            <td>{item.case_title || 'Unassigned'}</td>
                            <td className="timestamp-cell">{format(item.last_seen)}</td>
                            <td>
                              <DeleteButton
                                confirmMessage={`Delete indicator "${item.value}"?`}
                                onDelete={async () => {
                                  await api.delete(`/hdp/iocs/${item.indicator_id}`);
                                  setIndicators((prev) => prev.filter((row) => row.indicator_id !== item.indicator_id));
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Evidence;
